import React, { useRef, useState } from "react";

import { twJoin } from "tailwind-merge";

import { StringTextareaEditor } from "src/cases/caseView/blocks/cellEditors/StringTextareaEditor";

export const TextInputEditor: React.FC<{
  value: unknown;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
  originalType: "string" | "number" | "currency";
  hasLeftIcon?: boolean;
  columnKey: string;
  /** The user's explicit height (stored at open, updated by drags) —
   *  see useStringCellHeight. */
  floorHeight?: number;
  onContentHeightChange?: (height: number) => void;
}> = ({
  value,
  onChange,
  onBlur,
  originalType,
  hasLeftIcon,
  columnKey,
  floorHeight,
  onContentHeightChange,
}) => {
  const isNumeric = originalType === "number" || originalType === "currency";

  if (isNumeric) {
    return (
      <NumericInputEditor
        hasLeftIcon={hasLeftIcon}
        value={value}
        onBlur={onBlur}
        onChange={onChange}
      />
    );
  }

  return (
    <StringTextareaEditor
      columnKey={columnKey}
      floorHeight={floorHeight}
      hasLeftIcon={hasLeftIcon}
      onContentHeightChange={onContentHeightChange}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
    />
  );
};

// Plain decimal or scientific notation — keeps Number() from accepting
// hex/binary literals ("0x10") or the word "Infinity".
const DECIMAL_NUMBER = /^[-+]?(\d+(\.\d*)?|\.\d+)([eE][-+]?\d+)?$/;

const toNumberOrNull = (text: string): number | null => {
  if (!DECIMAL_NUMBER.test(text)) return null;
  const num = Number(text);
  return Number.isFinite(num) ? num : null;
};

/** Rebuilds "1..2" / "12abc000" from sign, digits, and first decimal point. */
const repairNumber = (text: string): string => {
  const sign = text.trimStart().startsWith("-") ? "-" : "";
  const [integer, ...decimals] = text.replace(/[^0-9.]/g, "").split(".");
  return decimals.length > 0
    ? `${sign}${integer}.${decimals.join("")}`
    : `${sign}${integer}`;
};

/** Turns the decimal separator into "." and drops grouping. Separators only
 *  count as grouping when the whole string is a valid grouped number;
 *  otherwise a single comma with no dot after it is the decimal point. */
const normalizeDecimalSeparators = (text: string): string => {
  const compact = text.replace(/[\s'’]/g, "");
  const toDecimal = (value: string): string =>
    value.replace(/\./g, "").replace(",", ".");

  // Valid comma grouping, e.g. "1,234" or "1,234,000.50".
  if (/^[-+]?[1-9]\d{0,2}(,\d{3})+(\.\d+)?$/.test(compact)) {
    return compact.replace(/,/g, "");
  }
  // Valid dot grouping, e.g. "1.234.567" or "1.234.567,89".
  if (/^[-+]?[1-9]\d{0,2}(\.\d{3}){2,}(,\d+)?$/.test(compact)) {
    return toDecimal(compact);
  }
  // Single comma as the decimal point ("99,99", "0,123", "1.234,56") —
  // unless a dot follows it ("1,34.56"), which marks the comma as grouping.
  const isDecimalComma =
    compact.split(",").length === 2 &&
    compact.lastIndexOf(",") > compact.lastIndexOf(".");
  return isDecimalComma ? toDecimal(compact) : compact.replace(/,/g, "");
};

/** The text as-is when valid (keeps scientific notation), otherwise rebuilt
 *  from digits only — a junk "e" must not become an exponent. */
const parseLooseNumber = (raw: string): number | null => {
  const normalized = normalizeDecimalSeparators(raw.trim());
  return toNumberOrNull(normalized) ?? toNumberOrNull(repairNumber(normalized));
};

const NumericInputEditor: React.FC<{
  value: unknown;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
  hasLeftIcon?: boolean;
}> = ({ value, onChange, onBlur, hasLeftIcon }) => {
  const [localValue, setLocalValue] = useState(() =>
    value === null || value === undefined ? "" : String(value),
  );
  const closingRef = useRef(false);

  const commitValue = () => {
    const num = parseLooseNumber(localValue);
    setLocalValue(num === null ? "" : String(num));
    onChange(num);
  };

  const handleCommit = () => {
    if (closingRef.current) return;
    commitValue();
    onBlur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        commitValue();
        if (e.shiftKey) {
          closingRef.current = true;
          return;
        }
        break;
      case "Escape":
        e.preventDefault();
        closingRef.current = true;
        onBlur();
        break;
      case "ArrowUp":
      case "ArrowDown":
      case "ArrowLeft":
      case "ArrowRight":
      case "Tab":
        return;
    }
    e.stopPropagation();
  };

  return (
    <input
      ref={(node) => node?.focus()}
      className={twJoin(
        "h-full w-full bg-transparent py-1.5 pr-2 text-right font-inter-normal-12px outline-none",
        hasLeftIcon ? "pl-6" : "pl-1.5",
      )}
      inputMode="decimal"
      type="text"
      value={localValue}
      onBlur={handleCommit}
      onChange={(e) => setLocalValue(e.target.value)}
      onKeyDown={handleKeyDown}
    />
  );
};
